import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useColectivos } from '../../hooks/useColectivos';
import { useStops } from '../../hooks/useStops';
import { ENDPOINTS } from '../../constants/endpoints';
import { ErrorMessage } from '../ErrorMessage';

export const AddStopScreen = () => {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const { colectivos, cargando, error, obtenerColectivos } = useColectivos();
  const { addStop } = useStops(folderId);

  const [codigoParada, setCodigoParada] = useState('');
  const [nombreParada, setNombreParada] = useState('');
  const [error2, setError2] = useState(null);
  const [validado, setValidado] = useState(false);
  const [linea, setLinea] = useState(null);

  useEffect(() => {
    if (colectivos && colectivos.length > 0) {
      setValidado(true);
    }
  }, [colectivos]);

  const manejarValidar = async () => {
    if (!codigoParada.trim()) {
      setError2('Ingresa el código de la parada');
      return;
    }

    if (!nombreParada.trim()) {
      setError2('Ingresa el nombre de la parada');
      return;
    }

    setError2(null);
    const codigoTrim = codigoParada.trim();
    const body = { idParada: codigoTrim, codLinea: '0' };
    
    // Intentar primero con unionplatense
    let endpointUsado = 'unionplatense';
    
    try {
      const response = await fetch(`${ENDPOINTS.unionplatense}`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const data = await response.json();
        let arrayData = null;
        
        // Si es un array directo
        if (Array.isArray(data)) {
          arrayData = data;
        } 
        // Si es un objeto que contiene un array
        else if (data && typeof data === 'object') {
          arrayData = Object.values(data).find(val => Array.isArray(val));
        }
        
        if (arrayData && arrayData.length > 0) {
          setLinea(endpointUsado);
          await obtenerColectivos(`${ENDPOINTS.unionplatense}`, 'POST', body);
          return;
        }
      }
    } catch (error) {
      console.error('Error con unionplatense:', error);
    }
    
    // Si unionplatense falla, intentar con linea7
    endpointUsado = 'linea7';
    
    try {
      const response = await fetch(`${ENDPOINTS.linea7}`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const data = await response.json();
        let arrayData = null;
        
        // Si es un array directo
        if (Array.isArray(data)) {
          arrayData = data;
        } 
        // Si es un objeto que contiene un array
        else if (data && typeof data === 'object') {
          arrayData = Object.values(data).find(val => Array.isArray(val));
        }
        
        if (arrayData && arrayData.length > 0) {
          setLinea(endpointUsado);
          await obtenerColectivos(`${ENDPOINTS.linea7}`, 'POST', body);
          return;
        } else {
          setError2('La parada no existe en ninguna línea');
          setValidado(false);
        }
      } else {
        setError2('La parada no existe en ninguna línea');
        setValidado(false);
      }
    } catch (error) {
      console.error('Error con linea7:', error);
      setError2('Error al validar la parada');
      setValidado(false);
    }
  };

  const manejarAgregar = () => {
    if (colectivos && colectivos.length > 0) {
      addStop(codigoParada.trim(), nombreParada.trim(), linea);
      navigate(`/folder/${folderId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/folder/${folderId}`)}
          className="p-2 hover:bg-gray-200 rounded-lg transition"
        >
          <ChevronLeft className="text-gray-700" size={24} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Agregar Parada</h1>
      </div>

      {error2 && <ErrorMessage message={error2} />}
      {error && <ErrorMessage message={error} />}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6 max-w-md">
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Código de la Parada
          </label>
          <input
            type="text"
            value={codigoParada}
            onChange={(e) => setCodigoParada(e.target.value)}
            placeholder="ej: LP2060"
            disabled={validado}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
          <p className="text-xs text-gray-500 mt-1">
            Podés encontrar el código en los carteles de las paradas
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Nombre de la Parada
          </label>
          <input
            type="text"
            value={nombreParada}
            onChange={(e) => setNombreParada(e.target.value)}
            placeholder="ej: Parada cerca de casa"
            disabled={validado}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </div>

        {!validado && (
          <button
            onClick={manejarValidar}
            disabled={cargando}
            className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg transition font-semibold"
          >
            {cargando ? 'Validando...' : 'Validar Parada'}
          </button>
        )}

        {validado && (
          <div className="space-y-2">
            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
              <p className="text-green-700 font-semibold">✓ Parada válida</p>
              <p className="text-green-600 text-sm">Se encontraron {colectivos.length} colectivo{colectivos.length !== 1 ? 's' : ''}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setValidado(false);
                  setCodigoParada('');
                  setNombreParada('');
                  setLinea(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition font-semibold"
              >
                Volver
              </button>
              <button
                onClick={manejarAgregar}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition font-semibold"
              >
                Agregar
              </button>
            </div>
          </div>
        )}

        {!validado && (
          <button
            onClick={() => navigate(`/folder/${folderId}`)}
            className="w-full mt-3 px-4 py-2 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition font-semibold"
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
};
